/* eslint-disable camelcase */
import { isNil } from 'lodash-es';
import type { ContextOfUse } from '../components/StatefulComponent/FilterChain/StatefulFilterChain';
import type { GlobalFilter } from '../domains/global-filters/model/FilterChain';
import { FilterKindEnum } from '../domains/global-filters/model/FilterChain';
import type { BaseSite } from '../domains/site/model';
import type { SegmentTrackData, SiteGroupingData } from '../domains/tracking/model';

export interface DataToTrack {
  globalFilter: GlobalFilter;
  segmentId?: number;
  site: BaseSite;
  contextOfUse: ContextOfUse;
}

function getSegmentType(segmentId?: number): 'unsaved' | 'predefined' | 'custom' {
  if (isNil(segmentId)) {
    return 'unsaved';
  }
  return +segmentId >= 0 ? 'custom' : 'predefined';
}

export const trackGlobalFilterApplied = (
  dataToTrack: DataToTrack,
  getGroupDataForSite: (site: BaseSite) => SiteGroupingData | undefined,
  track: (eventName: string, properties?: SegmentTrackData) => void,
): void => {
  if (dataToTrack.globalFilter.filterList.length === 0) {
    // We don't want to track global filters when there is no filters
    return;
  }
  const groupDataForSite = getGroupDataForSite(dataToTrack.site);

  const filters_included: Set<string> = new Set();
  const segment_type = getSegmentType(dataToTrack.segmentId);
  const tool = dataToTrack.contextOfUse.toolName;
  let user_attribute_filter_count = 0;
  const user_attribute_filter_names: Set<string> = new Set();
  let hasGaEvent = false;
  let hasMixpanelEvent = false;
  let hasOmniconvertEvent = false;

  dataToTrack.globalFilter.filterList.forEach((item) => {
    filters_included.add(item.kind);
    if (item.kind === FilterKindEnum.UserAttribute) {
      user_attribute_filter_count += 1;
      user_attribute_filter_names.add(item.value?.name as string);
    } else if (item.kind === FilterKindEnum.Event) {
      (item.value?.selectedValues || []).forEach((selectedValue) => {
        if (selectedValue.startsWith('GA_')) {
          hasGaEvent = true;
        } else if (selectedValue.startsWith('MP_')) {
          hasMixpanelEvent = true;
        } else if (selectedValue.includes('Omniconvert')) {
          hasOmniconvertEvent = true;
        }
      });
    }
  });

  const integrations: string[] = [];
  if (hasGaEvent) {
    integrations.push('ga');
  }
  if (hasMixpanelEvent) {
    integrations.push('mixpanel');
  }
  if (hasOmniconvertEvent) {
    integrations.push('omniconvert');
  }

  const commonPart = {
    ...groupDataForSite,
    filters_included: [...filters_included.values()].sort().join(', '),
    segment_type,
    tool,
  };

  track('Global Filters Applied', {
    ...commonPart,
    state_event_was_triggered_on: dataToTrack.contextOfUse.page,
    user_attribute_filter_count,
    user_attribute_filter_names: [...user_attribute_filter_names].sort().join(', '),
  });

  if (integrations.length > 0) {
    track('Integration Events Filter Applied', {
      ...commonPart,
      integrations,
    });
  }
};
